/**
 * Global dock coordination store (TASK-713).
 *
 * The slim cross-surface API that survived chatStore's deletion:
 * - `requestPrompt` — another surface hands the dock a message to send
 *   (TaskCard's "Work on this with AI"). Never persisted: a prompt left in
 *   storage would fire itself on the next load.
 * - `contextOverride` — a page supplies the entity the route cannot express
 *   (COReviewWorkspace: the route names the review, not its client).
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ActiveTaskThread, RouteEntity } from '../components/dock/dockScope';
import type { RequestedPrompt } from './createChatStore';

export interface DockContextOverride {
  entity?: RouteEntity;
  page_data?: Record<string, unknown>;
  /** Full route override (a popup's inner route, e.g. /meeting/:id/prepare). */
  current_route?: string;
}

interface DockState {
  /**
   * TASK-767: a bare string means "show what you send". A `{display, payload}`
   * pair means the two differ — the delegation payload carries routing
   * scaffolding and ids the advisor never typed.
   */
  pendingPrompt: RequestedPrompt | null;
  requestPrompt: (prompt: RequestedPrompt) => void;
  clearPendingPrompt: () => void;
  contextOverride: DockContextOverride | null;
  setContextOverride: (override: DockContextOverride | null) => void;
  /**
   * TASK-715: a page/popup that must supply LIVE context at send time (current
   * brief snapshot, portfolio, proposals) registers a provider; it wins over
   * the static override. Cleared on unmount by the registrar.
   */
  contextProvider: (() => DockContextOverride) | null;
  setContextProvider: (fn: (() => DockContextOverride) | null) => void;
  /**
   * TASK-769: the unit of work the copilot thread currently follows. Set by
   * delegation, never derived from a route. Not persisted — a thread left in
   * storage would silently re-scope the next session's conversation.
   */
  activeTaskThread: ActiveTaskThread | null;
  openTaskThread: (thread: ActiveTaskThread) => void;
  closeTaskThread: () => void;
}

export const useDockStore = create<DockState>()(
  devtools(
    (set) => ({
      pendingPrompt: null,
      requestPrompt: (pendingPrompt: RequestedPrompt) => set({ pendingPrompt }),
      clearPendingPrompt: () => set({ pendingPrompt: null }),
      contextOverride: null,
      setContextOverride: (contextOverride) => set({ contextOverride }),
      contextProvider: null,
      setContextProvider: (contextProvider) => set({ contextProvider }),
      activeTaskThread: null,
      openTaskThread: (activeTaskThread) => set({ activeTaskThread }),
      closeTaskThread: () => set({ activeTaskThread: null }),
    }),
    { name: 'DockStore' }
  )
);
